import { apiRequest, ApiResponse } from '../request'
import { downloadBlob } from '../../download-blob'
import { getStoredToken } from '@/lib/api/session-refresh'

// ======================== TYPES ========================

export type JournalEntryStatus = 'PENDING' | 'POSTED' | 'REVIEWED'
export type JournalEntryOrigin = 'AUTOMATIC' | 'AI_CLASSIFIED' | 'MANUAL'
export type JournalEntryLineType = 'DEBIT' | 'CREDIT'

export interface JournalEntryLine {
    id: number
    journalEntryId: number
    accountId: number
    type: JournalEntryLineType
    amount: number
    description: string | null
    account: {
        id: number
        code: string
        title: string
        type: string
    }
}

export interface JournalEntry {
    id: number
    companyId: number
    billingId: number | null
    date: string
    description: string
    status: JournalEntryStatus
    origin: JournalEntryOrigin
    accountingRuleId: number | null
    aiConfidence: number | null
    aiReasoning: string | null
    reviewedAt: string | null
    reviewNotes: string | null
    createdAt: string
    updatedAt: string
    lines: JournalEntryLine[]
    billing: {
        id: number
        description: string | null
        amount: number
        type: string
        status: string
        category: string | null
        paymentMethod: string | null
        dueDate: string
        paidAt: string | null
        client: { id: number; name: string } | null
        supplier: { id: number; tradingName: string } | null
    } | null
    accountingRule: { id: number; name: string } | null
    createdBy: { id: number; name: string } | null
    reviewedBy: { id: number; name: string } | null
}

export interface JournalEntryListResponse {
    items: JournalEntry[]
    total: number
    page: number
    limit: number
    totalPages: number
}

export interface AccountingRule {
    id: number
    companyId: number
    name: string
    description: string | null
    priority: number
    active: boolean
    billingType: string | null
    categoryContains: string | null
    descriptionContains: string | null
    hasConsultation: boolean | null
    hasExam: boolean | null
    hasProcedure: boolean | null
    hasOrder: boolean | null
    paymentMethod: string | null
    minAmount: number | null
    maxAmount: number | null
    debitAccountId: number
    creditAccountId: number
    debitAccount: { id: number; code: string; title: string; type: string }
    creditAccount: { id: number; code: string; title: string; type: string }
    createdAt: string
    updatedAt: string
}

export interface CreateJournalEntryData {
    billingId?: number
    date: string
    description: string
    lines: {
        accountId: number
        type: JournalEntryLineType
        amount: number
        description?: string
    }[]
}

export interface UpdateJournalEntryData {
    date?: string
    description?: string
    status?: JournalEntryStatus
    reviewNotes?: string
    lines?: {
        accountId: number
        type: JournalEntryLineType
        amount: number
        description?: string
    }[]
}

export interface CreateAccountingRuleData {
    name: string
    description?: string
    priority?: number
    billingType?: string
    categoryContains?: string
    descriptionContains?: string
    hasConsultation?: boolean
    hasExam?: boolean
    hasProcedure?: boolean
    hasOrder?: boolean
    paymentMethod?: string
    minAmount?: number
    maxAmount?: number
    debitAccountId: number
    creditAccountId: number
}

export interface UpdateAccountingRuleData extends Partial<CreateAccountingRuleData> {
    active?: boolean
}

// ======================== JOURNAL ENTRIES API ========================

export async function getJournalEntries(
    params?: {
        status?: JournalEntryStatus
        origin?: JournalEntryOrigin
        startDate?: string
        endDate?: string
        billingId?: number
        page?: number
        limit?: number
    },
): Promise<ApiResponse<JournalEntryListResponse>> {
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.origin) query.set('origin', params.origin)
    if (params?.startDate) query.set('startDate', params.startDate)
    if (params?.endDate) query.set('endDate', params.endDate)
    if (params?.billingId) query.set('billingId', String(params.billingId))
    if (params?.page) query.set('page', String(params.page))
    if (params?.limit) query.set('limit', String(params.limit))

    const qs = query.toString()
    return apiRequest<JournalEntryListResponse>(
        `financial/accounting/journal-entries${qs ? `?${qs}` : ''}`,
        {},
    )
}

export async function getJournalEntry(id: number): Promise<ApiResponse<JournalEntry>> {
    return apiRequest<JournalEntry>(`financial/accounting/journal-entries/${id}`, {})
}

export async function createJournalEntry(data: CreateJournalEntryData): Promise<ApiResponse<JournalEntry>> {
    return apiRequest<JournalEntry>('financial/accounting/journal-entries', { data }, 'post')
}

export async function updateJournalEntry(id: number, data: UpdateJournalEntryData): Promise<ApiResponse<JournalEntry>> {
    return apiRequest<JournalEntry>(`financial/accounting/journal-entries/${id}`, { data }, 'patch')
}

export async function deleteJournalEntry(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`financial/accounting/journal-entries/${id}`, {}, 'delete')
}

export async function reviewJournalEntries(ids: number[], notes?: string): Promise<ApiResponse<{ reviewed: number }>> {
    return apiRequest<{ reviewed: number }>('financial/accounting/journal-entries/review', { data: { ids, notes } }, 'post')
}

export async function processBillingAccounting(billingId: number): Promise<ApiResponse<JournalEntry>> {
    return apiRequest<JournalEntry>(`financial/accounting/journal-entries/process-billing/${billingId}`, {}, 'post')
}

// ======================== ACCOUNTING RULES API ========================

export async function getAccountingRules(): Promise<ApiResponse<AccountingRule[]>> {
    return apiRequest<AccountingRule[]>('financial/accounting/rules', {})
}

export async function getAccountingRule(id: number): Promise<ApiResponse<AccountingRule>> {
    return apiRequest<AccountingRule>(`financial/accounting/rules/${id}`, {})
}

export async function createAccountingRule(data: CreateAccountingRuleData): Promise<ApiResponse<AccountingRule>> {
    return apiRequest<AccountingRule>('financial/accounting/rules', { data }, 'post')
}

export async function updateAccountingRule(id: number, data: UpdateAccountingRuleData): Promise<ApiResponse<AccountingRule>> {
    return apiRequest<AccountingRule>(`financial/accounting/rules/${id}`, { data }, 'patch')
}

export async function deleteAccountingRule(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`financial/accounting/rules/${id}`, {}, 'delete')
}

// ======================== CONFIG API ========================

export async function getDefaultCashAccount(): Promise<ApiResponse<{ id: number; code: string; title: string; type: string } | null>> {
    return apiRequest('financial/accounting/config/default-cash-account', {})
}

export async function setDefaultCashAccount(accountId: number): Promise<ApiResponse<{ id: number; code: string; title: string; type: string }>> {
    return apiRequest('financial/accounting/config/default-cash-account', { data: { accountId } }, 'post')
}

export async function suggestAccountingRules(): Promise<ApiResponse<{
    suggestions: CreateAccountingRuleData[]
    message: string | null
}>> {
    return apiRequest('financial/accounting/rules/suggest', {}, 'post')
}

// ======================== REPORTS API ========================

export interface DREReportItem {
    accountId: number
    code: string
    title: string
    debit: number
    credit: number
    balance: number
    /** Participação na receita líquida. Null quando não houve receita. */
    verticalPercent?: number | null
    children?: DREReportItem[]
}

/** Cascata do resultado. `available` é false em plano fora do padrão SPED. */
export interface DREStructure {
    available: boolean
    grossRevenue: number
    deductions: number
    netRevenue: number
    costs: number
    grossProfit: number
    operatingExpenses: number
    otherOperatingRevenue: number
    otherExpenses: number
    ebitda: number
    depreciation: number
    ebit: number
    financialRevenue: number
    financialExpenses: number
    financialResult: number
    ebt: number
    taxes: number
    netIncome: number
    margins: {
        gross: number | null
        ebitda: number | null
        ebit: number | null
        net: number | null
    }
}

export interface DREReport {
    period: { startDate: string; endDate: string }
    mode?: 'flat' | 'sintetico' | 'analitico'
    income: DREReportItem[]
    expense: DREReportItem[]
    totalIncome: number
    totalExpense: number
    netResult: number
    structure?: DREStructure
}

export interface DREEvolutionMonth {
    month: string
    grossRevenue: number
    netRevenue: number
    costs: number
    operatingExpenses: number
    ebitda: number
    netIncome: number
    ebitdaMargin: number | null
    netMargin: number | null
}

export interface DREEvolutionReport {
    period: { startDate: string; endDate: string }
    months: DREEvolutionMonth[]
}

export interface DREComparativoReport {
    current: DREReport
    previous: DREReport
    variance: {
        totalIncome: number
        totalExpense: number
        netResult: number
        totalIncomePercent: number
        totalExpensePercent: number
        netResultPercent: number
    }
}

export interface BalanceteAccount {
    accountId: number
    code: string
    title: string
    type: string
    /** Saldo acumulado antes do início do período. */
    previousBalance?: number
    debit: number
    credit: number
    /** Saldo do PERÍODO (débito − crédito). */
    balance: number
    /** Saldo anterior + movimento do período. */
    finalBalance?: number
}

export interface BalanceteReport {
    period: { startDate: string; endDate: string }
    accounts: BalanceteAccount[]
    totalDebit: number
    totalCredit: number
    /** Em partidas dobradas tem que ser zero. */
    difference?: number
    totalsByType?: Record<
        string,
        { debit: number; credit: number; finalBalance: number }
    >
}

export interface RazaoReport {
    account: { id: number; code: string; title: string; type: string }
    period: { startDate: string; endDate: string }
    movements: {
        date: string
        journalEntryId: number
        description: string
        origin: string
        billingId: number | null
        type: string
        amount: number
        balance: number
    }[]
    totalDebit: number
    totalCredit: number
    finalBalance: number
}

export interface FluxoCaixaReport {
    period: { startDate: string; endDate: string }
    groupBy: string
    data: { period: string; income: number; expense: number }[]
    totalIncome: number
    totalExpense: number
    netCashFlow: number
}

export interface AccountingDashboard {
    period: { startDate: string; endDate: string }
    totals: { entries: number; pending: number; posted: number; reviewed: number }
    origins: { automatic: number; ai: number; manual: number }
    financials: {
        totalIncome: number
        totalExpense: number
        netResult: number
        ebitda?: number
        breakdown?: {
            fromJournalEntries?: { income: number; expense: number }
            fromBillingFallback?: { income: number; expense: number; count: number }
            financialExpense?: number
            tax?: number
            depreciation?: number
        }
    }
    alerts: { pendingEntries: number; unclassifiedBillings: number }
    recentPending: any[]
}

export interface BalancoPatrimonialAccount {
    accountId: number
    code: string
    title: string
    type: string
    balance: number
    parentId: number | null
}

export interface BalancoPatrimonialReport {
    asOfDate: string
    asset: { accounts: BalancoPatrimonialAccount[]; total: number }
    liability: { accounts: BalancoPatrimonialAccount[]; total: number }
    equity: {
        accounts: BalancoPatrimonialAccount[]
        netResultOfPeriod: number
        total: number
    }
    totals: {
        asset: number
        liabilityAndEquity: number
        balanced: boolean
        difference: number
    }
}

export interface OnboardingStatus {
    hasChartOfAccounts: boolean
    chartOfAccountsCount: number
    hasRules: boolean
    rulesCount: number
    hasDefaultCashAccount: boolean
    defaultCashAccountId: number | null
    completed: boolean
}

export async function getDREReport(
    startDate: string,
    endDate: string,
    options?: {
        costCenterId?: number
        mode?: 'sintetico' | 'analitico'
        compareStartDate?: string
        compareEndDate?: string
    },
): Promise<ApiResponse<DREReport | DREComparativoReport>> {
    const params: any = { startDate, endDate }
    if (options?.costCenterId) params.costCenterId = options.costCenterId
    if (options?.mode) params.mode = options.mode
    if (options?.compareStartDate) params.compareStartDate = options.compareStartDate
    if (options?.compareEndDate) params.compareEndDate = options.compareEndDate
    return apiRequest<DREReport | DREComparativoReport>('financial/accounting/reports/dre', { params })
}

export async function getDREEvolution(
    startDate: string,
    endDate: string,
    costCenterId?: number,
): Promise<ApiResponse<DREEvolutionReport>> {
    const params: any = { startDate, endDate }
    if (costCenterId) params.costCenterId = costCenterId
    return apiRequest<DREEvolutionReport>('financial/accounting/reports/dre-evolution', { params })
}

export async function getBalanceteReport(startDate: string, endDate: string): Promise<ApiResponse<BalanceteReport>> {
    return apiRequest<BalanceteReport>(`financial/accounting/reports/balancete?startDate=${startDate}&endDate=${endDate}`, {})
}

export async function getRazaoReport(accountId: number, startDate: string, endDate: string): Promise<ApiResponse<RazaoReport>> {
    return apiRequest<RazaoReport>(`financial/accounting/reports/razao/${accountId}?startDate=${startDate}&endDate=${endDate}`, {})
}

export async function getFluxoCaixaReport(startDate: string, endDate: string, groupBy?: string): Promise<ApiResponse<FluxoCaixaReport>> {
    const gb = groupBy ? `&groupBy=${groupBy}` : ''
    return apiRequest<FluxoCaixaReport>(`financial/accounting/reports/fluxo-caixa?startDate=${startDate}&endDate=${endDate}${gb}`, {})
}

export async function getAccountingDashboard(startDate: string, endDate: string): Promise<ApiResponse<AccountingDashboard>> {
    return apiRequest<AccountingDashboard>(`financial/accounting/dashboard?startDate=${startDate}&endDate=${endDate}`, {})
}

export async function getBalancoPatrimonialReport(
    asOfDate: string,
): Promise<ApiResponse<BalancoPatrimonialReport>> {
    return apiRequest<BalancoPatrimonialReport>(
        `financial/accounting/reports/balanco-patrimonial?asOfDate=${asOfDate}`,
        {},
    )
}

export async function getAccountingOnboardingStatus(): Promise<ApiResponse<OnboardingStatus>> {
    return apiRequest<OnboardingStatus>('financial/accounting/onboarding/status', {})
}

export async function seedDefaultChartOfAccounts(
    replace: boolean = false,
): Promise<
    ApiResponse<{ created: number; alreadySeeded: boolean; replacedCount?: number }>
> {
    const qs = replace ? '?replace=true' : ''
    return apiRequest<{
        created: number
        alreadySeeded: boolean
        replacedCount?: number
    }>(`financial/chart-of-accounts/seed-default${qs}`, {}, 'post')
}

export async function seedDefaultAccountingRules(): Promise<
    ApiResponse<{ created: number; alreadySeeded: boolean }>
> {
    return apiRequest<{ created: number; alreadySeeded: boolean }>(
        'financial/accounting/rules/seed-default',
        {},
        'post',
    )
}

export async function getReprocessStatus(): Promise<ApiResponse<{ count: number }>> {
    return apiRequest<{ count: number }>('financial/accounting/reprocess-status', {})
}

export async function reprocessUnclassifiedBillings(
    limit: number = 500,
): Promise<
    ApiResponse<{
        processed: number
        autoClassified: number
        pending: number
    }>
> {
    return apiRequest<{ processed: number; autoClassified: number; pending: number }>(
        `financial/accounting/reprocess-unclassified?limit=${limit}`,
        {},
        'post',
    )
}

export async function reprocessAllBackground(): Promise<
    ApiResponse<{
        enqueued: boolean
        alreadyRunning?: boolean
        jobId: string
        state?: string
    }>
> {
    return apiRequest('financial/accounting/reprocess-all-background', {}, 'post')
}

export interface ReprocessJobStatus {
    exists: boolean
    jobId?: string
    state?: 'waiting' | 'active' | 'delayed' | 'completed' | 'failed' | 'paused'
    progress?: any
    attemptsMade?: number
    failedReason?: string | null
    finishedOn?: number | null
    processedOn?: number | null
    returnvalue?: {
        totalProcessed: number
        iterations: number
        autoClassified: number
        pending: number
    } | null
}

export async function getReprocessJobStatus(): Promise<ApiResponse<ReprocessJobStatus>> {
    return apiRequest<ReprocessJobStatus>(
        'financial/accounting/reprocess-job-status',
        {},
    )
}

// ======================== EXPORTAÇÃO ========================

async function downloadFile(path: string, filename: string): Promise<void> {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const token = getStoredToken() || ''

    const response = await fetch(`${baseUrl}/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!response.ok) throw new Error('Erro ao exportar relatório')

    const blob = await response.blob()
    downloadBlob(blob, filename)
}

export async function exportDRE(startDate: string, endDate: string, format: 'xlsx' | 'pdf', options?: { costCenterId?: number; mode?: string }): Promise<void> {
    const params = new URLSearchParams({ startDate, endDate, format })
    if (options?.costCenterId) params.set('costCenterId', String(options.costCenterId))
    if (options?.mode) params.set('mode', options.mode)
    await downloadFile(`financial/accounting/export/dre?${params}`, `DRE_${startDate}_${endDate}.${format}`)
}

export async function exportBalancete(startDate: string, endDate: string, format: 'xlsx' | 'pdf', costCenterId?: number): Promise<void> {
    const params = new URLSearchParams({ startDate, endDate, format })
    if (costCenterId) params.set('costCenterId', String(costCenterId))
    await downloadFile(`financial/accounting/export/balancete?${params}`, `Balancete_${startDate}_${endDate}.${format}`)
}

export async function exportRazao(accountId: number, startDate: string, endDate: string, format: 'xlsx' | 'pdf'): Promise<void> {
    const params = new URLSearchParams({ startDate, endDate, format })
    await downloadFile(`financial/accounting/export/razao/${accountId}?${params}`, `Razao_${accountId}_${startDate}_${endDate}.${format}`)
}

export async function exportFluxoCaixa(startDate: string, endDate: string, format: 'xlsx' | 'pdf', groupBy?: string, bankAccountId?: number): Promise<void> {
    const params = new URLSearchParams({ startDate, endDate, format })
    if (groupBy) params.set('groupBy', groupBy)
    if (bankAccountId) params.set('bankAccountId', String(bankAccountId))
    await downloadFile(`financial/accounting/export/fluxo-caixa?${params}`, `FluxoCaixa_${startDate}_${endDate}.${format}`)
}

export async function exportLivroDiario(startDate: string, endDate: string, format: 'xlsx' | 'pdf'): Promise<void> {
    const params = new URLSearchParams({ startDate, endDate, format })
    await downloadFile(`financial/accounting/export/livro-diario?${params}`, `LivroDiario_${startDate}_${endDate}.${format}`)
}

export async function exportPlanoDeContas(format: 'xlsx' | 'pdf'): Promise<void> {
    await downloadFile(`financial/accounting/export/plano-de-contas?format=${format}`, `PlanoDeContas.${format}`)
}

export async function exportSpedEcd(year: number, options?: { accountantName?: string; accountantCpf?: string; accountantCrc?: string }): Promise<void> {
    const params = new URLSearchParams({ year: String(year) })
    if (options?.accountantName) params.set('accountantName', options.accountantName)
    if (options?.accountantCpf) params.set('accountantCpf', options.accountantCpf)
    if (options?.accountantCrc) params.set('accountantCrc', options.accountantCrc)
    await downloadFile(`financial/accounting/export/sped-ecd?${params}`, `SPED_ECD_${year}.txt`)
}
